import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Loader2, FolderCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

export default function SubmittalsStep({ project }) {
  const [submittals, setSubmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    if (!project?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${API}/projects/${project.id}/project-submittals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmittals(res.data?.submittals || res.data || []);
      } catch (e) {
        console.error('Failed to load submittals:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project?.id]);

  const handleDownload = async (sub) => {
    setDownloading(prev => ({ ...prev, [sub.id]: true }));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(
        `${API}/projects/${project.id}/project-submittals/${sub.id}/download-latest`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await res.blob();
      const fname = res.headers.get('X-Filename')
        || (sub.name || 'Submittal') + '.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error:', e);
      toast.error(e.message || 'Failed to download submittal');
    } finally {
      setDownloading(prev => ({ ...prev, [sub.id]: false }));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const finalSubmittals = submittals.filter(s => s.package_url || s.annotation_version > 0);

  return (
    <div className="space-y-4" data-testid="submittals-step">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FolderCheck className="h-5 w-5 text-primary" />
          Final Submittals
        </h3>
        <p className="text-sm text-muted-foreground">Save submittals to your computer.</p>
      </div>

      {finalSubmittals.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <FolderCheck className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No final submittals yet. Complete the Annotate step to generate your finished submittals.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {finalSubmittals.map(sub => {
            const version = sub.annotation_version || 0;
            const isDownloading = downloading[sub.id];

            return (
              <Card key={sub.id} data-testid={`final-submittal-${sub.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{sub.name || sub.submittal_name || 'Submittal'}</p>
                      <p className="text-xs text-muted-foreground">
                        {sub.spec_section || 'No spec section'}
                        {version > 0 && <span className="ml-2">v{version}</span>}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(sub)}
                      disabled={isDownloading}
                      data-testid={`save-as-submittal-${sub.id}`}
                    >
                      {isDownloading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Download className="h-4 w-4 mr-1.5" />
                      )}
                      {isDownloading ? 'Downloading...' : 'Save As'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
